<template>
  <footer class="play-footer" aria-label="Game telemetry">
    <div class="play-footer-copy">
      <div class="play-metrics" title="Package · Lua input state · raw market coins · position lifecycles · first frame · session duration">
        <span>Pkg {{ sizeLabel }}</span><b>|</b><span>State {{ stateLabel }}</span><b>|</b><span>Coins {{ coinLabel }}</span><b>|</b><span>Positions {{ positionLabel }}</span><b>|</b><span>Start {{ startupLabel }}</span><b>|</b><span>Time {{ elapsedLabel }}</span>
      </div>
      <small v-if="versionText" class="play-version">{{ versionText }}</small>
    </div>
  </footer>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

const props = defineProps({
  metrics: { type: Object, default: () => ({}) },
  release: { type: String, default: '' },
  draft: { type: String, default: '' },
  deploy: { type: String, default: '' },
  sdk: { type: String, default: 'aime.ui-plugin.sdk.v1' },
})

const elapsed = ref(0)
let startedAt = 0
let timer = 0
const sizeLabel = computed(() => formatSize(props.metrics?.bytes))
const stateLabel = computed(() => formatSize(props.metrics?.snapshotBytes))
const coinLabel = computed(() => formatCount(props.metrics?.coins))
const positionLabel = computed(() => formatCount(props.metrics?.positions))
const startupLabel = computed(() => formatStartup(props.metrics?.startupMs))
const elapsedLabel = computed(() => formatElapsed(elapsed.value))
const versionText = computed(() => {
  const content = props.draft ? `draft: ${props.draft}` : props.release ? `release: ${props.release}` : ''
  const deploy = props.deploy ? `deploy: ${props.deploy}` : ''
  const sdk = props.sdk ? `sdk: ${props.sdk}` : ''
  return [content, deploy, sdk].filter(Boolean).join(' | ')
})

onMounted(() => {
  startedAt = Date.now()
  timer = window.setInterval(() => { elapsed.value = Date.now() - startedAt }, 1000)
})
onBeforeUnmount(() => { if (timer) window.clearInterval(timer) })

function formatSize(bytes) {
  const value = Math.max(0, Number(bytes) || 0)
  if (value < 1024) return `${Math.round(value)} B`
  if (value < 1048576) return `${(value / 1024).toFixed(value >= 10240 ? 0 : 1)} KB`
  const mb = value / 1048576
  return `${mb >= 10 ? mb.toFixed(0) : mb.toFixed(1)} MB`
}
function formatCount(value) {
  const count = Math.max(0, Math.floor(Number(value) || 0))
  return new Intl.NumberFormat('en', { notation: count >= 1000 ? 'compact' : 'standard', maximumFractionDigits: 1 }).format(count)
}
function formatStartup(ms) {
  const seconds = Math.max(0, Number(ms) || 0) / 1000
  return `${seconds.toFixed(1)}s`
}
function formatElapsed(ms) {
  const total = Math.max(0, Math.floor((Number(ms) || 0) / 1000))
  const minutes = Math.floor(total / 60)
  return `${String(minutes).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}
</script>

<style scoped>
.play-footer{min-width:0;flex:0 0 auto;min-height:30px;display:grid;place-items:center;padding:0 6px;border-top:1px solid #202020;background:#0d0d0d;color:#94a3b8;font-variant-numeric:tabular-nums}
.play-footer-copy{min-width:0;width:100%;display:grid;grid-template-rows:16px 9px;align-items:center;justify-items:center;text-align:center}
.play-metrics{min-width:0;max-width:100%;display:flex;align-items:center;justify-content:center;gap:3px;overflow:hidden;color:#657180;font:6.5px/1 ui-monospace,SFMono-Regular,Menlo,monospace;white-space:nowrap}
.play-metrics b{color:#3f4854;font-weight:400}
.play-version{min-width:0;max-width:100%;display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#596472;font:7px/8px ui-monospace,SFMono-Regular,Menlo,monospace}
@media(max-width:420px){.play-footer{min-height:28px;padding:0 4px}.play-footer-copy{grid-template-rows:15px 8px}.play-metrics{gap:2px;font-size:5.5px}.play-version{font-size:5.5px;line-height:7px}}
</style>
